<script lang="ts">
  // Grid for a local source: a folder of the user's own .rfa files. Scanned cheaply (name + size),
  // with the full detail (3D, parameters) read on demand when a card is opened.

  import { transport } from "@/transport";
  import { sources } from "./sourcesStore.svelte";
  import { categoryLabel } from "./categories";
  import LocalFamilyDetail from "./LocalFamilyDetail.svelte";
  import ViewSwitcher, { gridClassFor } from "./ViewSwitcher.svelte";
  import { catalog } from "./catalogStore.svelte";
  import type { LocalFamily, ScanResult } from "./types";
  import {
    getCachedLocalMeta,
    getCachedThumbnail,
    localPackageVersion,
    setCachedThumbnail,
    type LocalCardMeta,
  } from "./familyCache";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";
  import { AppBadge, AppButton } from "@/components/ui";

  let { sourceId }: { sourceId: string } = $props();

  let families = $state<LocalFamily[]>([]);
  let status = $state<"loading" | "ready" | "error">("loading");
  let errorMessage = $state("");
  let truncated = $state(false);
  let open = $state<LocalFamily | null>(null);

  // Category filtering. A local .rfa's category is only known by opening it, so it is read on demand
  // (see ReadLocalCategory) rather than during the cheap scan: the user asks to sort by category, and
  // the folder is read through one file at a time, cached, with a running progress count.
  let categoryByPath = $state<Record<string, string>>({});
  let selectedCategory = $state<string | null>(null);
  let detecting = $state(false);
  let detectDone = $state(0);
  // Bumped on every (re)scan so a detection loop for a stale folder stops itself.
  let detectEpoch = 0;

  const categories = $derived.by(() => {
    const set = new Set<string>();
    for (const path in categoryByPath) {
      const c = categoryByPath[path];
      if (c) set.add(c);
    }
    return [...set].sort();
  });

  // ── The folder tree ────────────────────────────────────────────────────────────────────────────
  //
  // The scan is recursive and comes back flat, which threw away the one thing a hand-kept library
  // has: its shelves. A folder of a thousand families is a wall; the same thousand arranged in
  // twenty folders is a library, and the person who arranged them did so on purpose.
  //
  // Built here, from the paths the scan already carries — no second host call, and nothing to keep
  // in sync with the filesystem beyond what a rescan already refreshes.

  /** Where in the tree we are, as segments below the source root. Empty is the root itself. */
  let folder = $state<string[]>([]);

  const rootPath = $derived(sources.selected().path ?? "");

  /** Each family with the folder segments it sits in, relative to the source root. */
  const placed = $derived.by(() => {
    const matching = selectedCategory
      ? families.filter((f) => categoryByPath[f.path] === selectedCategory)
      : families;
    return matching.map((f) => ({ family: f, segments: folderSegments(rootPath, f.path) }));
  });

  /** True when a family sits in the folder currently open, or anywhere beneath it. */
  const under = (segments: string[]) =>
    folder.every((seg, i) => segments[i]?.toLowerCase() === seg.toLowerCase());

  /** Subfolders directly below the current one, each with everything beneath it counted. A folder
   *  whose whole subtree is filtered out disappears rather than offering an empty room. */
  const subfolders = $derived.by(() => {
    const counts = new Map<string, number>();
    for (const p of placed) {
      if (p.segments.length <= folder.length || !under(p.segments)) continue;
      // Guarded for the type checker's sake: the length test above already guarantees a segment
      // here, but an indexed read is `string | undefined` under noUncheckedIndexedAccess.
      const name = p.segments[folder.length];
      if (!name) continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts].sort((a, b) => a[0].localeCompare(b[0]));
  });

  /** Families in this folder itself — not in its subfolders, which have their own rooms. */
  const visibleFamilies = $derived(
    placed.filter((p) => p.segments.length === folder.length && under(p.segments)).map((p) => p.family),
  );

  /**
   * The folders between the source root and a file, as segments.
   *
   * Split on both separators: the paths come from Windows, but a UNC share or a path the user typed
   * can carry either, and one stray forward slash would otherwise put a family in a folder named
   * "sub/deeper".
   */
  function folderSegments(root: string, filePath: string): string[] {
    const normalise = (s: string) => s.replace(/\\/g, "/").replace(/\/+$/, "");
    const full = normalise(filePath);
    const base = normalise(root);

    const relative = full.toLowerCase().startsWith(base.toLowerCase() + "/")
      ? full.slice(base.length + 1)
      : full;

    // The last segment is the file itself.
    return relative.split("/").slice(0, -1).filter(Boolean);
  }

  function openFolder(name: string) {
    folder = [...folder, name];
  }

  /** Up to a crumb; -1 is the source root. */
  function goToDepth(depth: number) {
    folder = folder.slice(0, depth + 1);
  }

  // The same density setting the cloud catalog uses, not a second one of its own: a folder and the
  // catalog are the same grid of the same kind of thing, and having to set "list" twice depending on
  // which source is selected would read as the setting not sticking.
  const view = $derived(catalog.state.view);
  const gridClass = $derived(gridClassFor(view));

  // ── Card thumbnails ────────────────────────────────────────────────────────────────────────────
  //
  // The preview Revit embedded in each .rfa, read through the Windows shell (see
  // ReadLocalThumbnails) — no Revit API, so scrolling a folder cannot stall the model.
  //
  // Fetched only for cards that actually come into view, and in batches. A folder here can hold two
  // thousand families; asking for every thumbnail up front would send megabytes for cards nobody
  // scrolls to, and asking per card would be a request per tile.

  let thumbByPath = $state<Record<string, string>>({});

  // Revit year and unit system, for cards whose family has been opened at some point. Both come from
  // reading the .rfa itself, which is far too expensive to do for a whole folder — so a card shows
  // them once it has them and its file size until then, rather than the grid stalling on a read
  // nobody asked for. Purely a cache read, no host round-trip.
  let metaByPath = $state<Record<string, LocalCardMeta>>({});

  /** Paths seen by the observer but not yet asked for, with the cache version to store them under. */
  let wanted = new Map<string, string>();
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let observer: IntersectionObserver | null = null;
  const cardFamily = new WeakMap<Element, LocalFamily>();

  /** Attached to each card's image area: asks for that card's thumbnail once it nears the viewport. */
  function lazyThumbnail(family: LocalFamily) {
    return (node: Element) => {
      cardFamily.set(node, family);
      observer ??= new IntersectionObserver(onIntersect, { rootMargin: "300px" });
      observer.observe(node);
      return () => observer?.unobserve(node);
    };
  }

  function onIntersect(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const family = cardFamily.get(entry.target);
      if (!family) continue;
      // One look is enough — the thumbnail does not change while the folder is on screen.
      observer?.unobserve(entry.target);
      void requestCardDetail(family);
    }
  }

  /**
   * The year and units for one card, from the cache the detail dialog fills when a family is opened.
   *
   * Kept separate from the thumbnail below, and callable again at any time, because the two become
   * available at completely different moments: the thumbnail as soon as the card is scrolled into
   * view, the year only once somebody has opened that family — which is usually long after. Folding
   * it into the thumbnail's run-once guard meant a card that already had its picture never went back
   * for its year, so the subtitle stayed on the file size forever.
   */
  async function loadCardMeta(family: LocalFamily) {
    const version = localPackageVersion(family.sizeBytes, family.modifiedTicks);
    const meta = await getCachedLocalMeta(family.path, version);
    if (meta) metaByPath[family.path] = meta;
  }

  async function requestCardDetail(family: LocalFamily) {
    void loadCardMeta(family);

    if (thumbByPath[family.path] || wanted.has(family.path)) return;

    const version = localPackageVersion(family.sizeBytes, family.modifiedTicks);
    const cached = await getCachedThumbnail(family.path, version);
    if (cached) {
      thumbByPath[family.path] = cached;
      return;
    }

    wanted.set(family.path, version);
    // Coalesced: scrolling brings a dozen cards into view within a few frames, and one request for
    // all of them beats a dozen.
    flushTimer ??= setTimeout(() => {
      flushTimer = null;
      void flushThumbnails();
    }, 60);
  }

  async function flushThumbnails() {
    // Matches the per-call cap on the host; the rest goes round again below.
    const batch = [...wanted].slice(0, 40);
    if (!batch.length) return;
    for (const [path] of batch) wanted.delete(path);

    const epoch = detectEpoch;
    try {
      const res = await transport.invoke<{ ok: boolean; items: { path: string; pngBase64: string }[] }>(
        "ReadLocalThumbnails",
        { paths: batch.map(([path]) => path) },
      );
      // A rescan while this was in flight means these belong to a folder that is no longer shown.
      if (epoch !== detectEpoch) return;

      const versions = new Map(batch);
      for (const item of res?.items ?? []) {
        thumbByPath[item.path] = item.pngBase64;
        const version = versions.get(item.path);
        if (version) void setCachedThumbnail(item.path, version, item.pngBase64);
      }
    } catch {
      // A thumbnail is decoration; a card without one still opens. Leaving the placeholder is the
      // whole of the error handling this needs.
    }

    if (wanted.size)
      flushTimer ??= setTimeout(() => {
        flushTimer = null;
        void flushThumbnails();
      }, 0);
  }

  async function scan() {
    status = "loading";
    open = null;
    detectEpoch++;
    detecting = false;
    categoryByPath = {};
    selectedCategory = null;

    // The previous folder's thumbnails, and anything still queued for it, belong to cards that are
    // about to be replaced.
    thumbByPath = {};
    metaByPath = {};
    folder = [];
    wanted.clear();
    observer?.disconnect();
    observer = null;
    try {
      const res = await transport.invoke<ScanResult>("ScanLocalSource", { id: sourceId });
      if (!res.ok) {
        errorMessage = res.error ?? t("local.scanFailed");
        status = "error";
        return;
      }
      families = res.families;
      truncated = res.truncated;
      status = "ready";
    } catch (error) {
      errorMessage = error instanceof Error ? error.message : String(error);
      status = "error";
    }
  }

  /**
   * Past this many families, sorting by category is worth warning about rather than just starting.
   *
   * It is not a rendering nicety: a local .rfa only tells you its category by being opened in a
   * background document on the Revit thread, so this reads every file in the folder, one at a time,
   * at roughly a second each. Fifty is where "a moment" turns into a minute — and where somebody who
   * pointed at a library of two thousand deserves to be told what they just asked for before Revit
   * spends half an hour on it.
   */
  const CATEGORY_SCAN_WARNING_THRESHOLD = 50;

  /** Set when the button was pressed on a folder large enough to warrant asking first. */
  let confirmDetect = $state(false);

  /** Families still without a known category — the ones this would actually open. */
  const undetectedCount = $derived(families.filter((f) => categoryByPath[f.path] === undefined).length);

  function requestDetectCategories() {
    if (undetectedCount > CATEGORY_SCAN_WARNING_THRESHOLD) {
      confirmDetect = true;
      return;
    }
    void detectCategories();
  }

  /** Abandons a run in flight. The loop checks the epoch between files, so it stops at the next
   *  one rather than after the whole folder — the same mechanism a rescan already uses. */
  function stopDetecting() {
    detectEpoch++;
    detecting = false;
  }

  async function detectCategories() {
    if (detecting) return;
    confirmDetect = false;
    detecting = true;
    detectDone = 0;
    const epoch = detectEpoch;
    try {
      for (const f of families) {
        if (epoch !== detectEpoch) return; // folder changed under us — abandon this run
        if (categoryByPath[f.path] === undefined) {
          try {
            const res = await transport.invoke<{ ok: boolean; category: string | null }>("ReadLocalCategory", {
              path: f.path,
            });
            categoryByPath = { ...categoryByPath, [f.path]: res.ok ? res.category ?? "" : "" };
          } catch {
            categoryByPath = { ...categoryByPath, [f.path]: "" };
          }
        }
        detectDone++;
      }
    } finally {
      if (epoch === detectEpoch) detecting = false;
    }
  }

  /** Closing the detail dialog is the one moment a card's year and units can have just become
   * known — the dialog is what reads the family and caches them. */
  function closeDetail() {
    const closed = open;
    open = null;
    if (closed) void loadCardMeta(closed);
  }

  function selectCategory(category: string | null) {
    selectedCategory = selectedCategory === category ? null : category;
  }

  /**
   * What a card says under its name: which Revit saved the family and in what units, once that is
   * known, and its size until then.
   *
   * The size is not a placeholder nobody wants — it is the only thing a folder scan can know without
   * opening every file, and it is what tells a heavy family from a light one at a glance. It gives
   * way to the year and units because those are what actually decide whether a family is usable in
   * this session, which is the same reason the cloud catalog leads with them.
   */
  function subtitle(f: LocalFamily): string {
    const meta = metaByPath[f.path];
    const parts = [
      meta?.revitYear ? `Revit ${meta.revitYear}` : null,
      meta?.unitSystem || null,
    ].filter(Boolean);

    return parts.length ? parts.join(" · ") : sizeLabel(f.sizeBytes);
  }

  function sizeLabel(bytes: number): string {
    return bytes > 1_000_000 ? `${(bytes / 1_048_576).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
  }

  async function removeSource() {
    await sources.remove(sourceId); // selection falls back to cloud; LibraryBrowser re-renders
  }

  // Runs once on mount, then again whenever sourceId changes — the union of Vue's onMounted(scan)
  // and watch(sourceId, scan) in one effect, since scan() already reads sourceId before its first
  // await.
  $effect(() => {
    void sourceId;
    void scan();
  });
</script>

<div class="flex flex-col gap-4">
  <!-- Source header: path + count + actions -->
  <div class="flex items-center gap-3 text-sm">
    <span class="text-ink-muted flex-1 min-w-0 truncate" title={sources.selected().path ?? ""}>
      {sources.selected().path}
    </span>
    <ViewSwitcher value={view} onchange={(v) => catalog.setView(v)} />
    <AppButton variant="secondary" size="xs" onclick={scan} class="shrink-0">
      {t("local.rescan")}
    </AppButton>
    <AppButton variant="danger" size="xs" onclick={removeSource} class="shrink-0">
      {t("local.removeSource")}
    </AppButton>
  </div>

  <!-- The warning before a long read. Inline rather than a modal: it belongs where the button was,
       it has to state a number to be worth anything, and a modal for something the user can simply
       not do is a bigger interruption than the thing it is warning about. -->
  {#if confirmDetect}
    <div class="flex flex-col gap-2 rounded-surface border border-warning bg-panel px-3 py-2.5">
      <p class="text-sm text-ink">{t("local.detectWarning", { count: undetectedCount })}</p>
      <div class="flex items-center gap-2">
        <AppButton variant="secondary" size="xs" onclick={() => void detectCategories()}>
          {t("local.detectAnyway")}
        </AppButton>
        <AppButton variant="ghost" size="xs" onclick={() => (confirmDetect = false)}>
          {t("publish.close")}
        </AppButton>
      </div>
    </div>
  {/if}

  <!-- Category sort/filter — reads each family's category on demand, then lets the grid be filtered. -->
  {#if status === "ready" && families.length}
    <div class="flex items-center gap-2 flex-wrap">
      {#if !categories.length && !detecting && !confirmDetect}
        <button
          class="text-xs rounded-full border border-dashed border-line px-3 py-1.5 hover:border-accent hover:text-accent transition-colors"
          onclick={requestDetectCategories}
        >
          {t("local.detectCategories")}
        </button>
      {/if}

      {#if detecting}
        <span class="text-xs text-ink-muted">
          {t("local.detecting", { done: detectDone, total: families.length })}
        </span>
        <!-- Stoppable, because the warning would be hollow otherwise: telling someone this takes a
             long time and then giving them no way out is worse than not warning at all. -->
        <button
          class="text-xs rounded-full border border-line px-3 py-1.5 hover:border-error hover:text-error transition-colors"
          onclick={stopDetecting}
        >
          {t("local.stopDetecting")}
        </button>
      {/if}

      {#if categories.length}
        <span class="text-xs text-ink-muted w-16 shrink-0">{t("catalog.category")}</span>
        <AppBadge selected={selectedCategory === null} onclick={() => selectCategory(null)}>
          {t("local.allCategories")}
        </AppBadge>
        {#each categories as c (c)}
          <AppBadge selected={selectedCategory === c} onclick={() => selectCategory(c)}>
            {categoryLabel(c, locale.value as SupportedLocale)}
          </AppBadge>
        {/each}
      {/if}
    </div>
  {/if}

  <!-- Where we are in the folder tree, and the way back. Shown only once there is somewhere to go
       back to: at the root of a flat folder it would be a crumb trail with one crumb. -->
  {#if status === "ready" && folder.length}
    <nav class="flex items-center gap-1 text-sm flex-wrap" aria-label={t("catalog.breadcrumb")}>
      <button
        class="p-1 rounded text-ink-muted hover:text-accent transition-colors"
        aria-label={t("local.allFolders")}
        title={t("local.allFolders")}
        onclick={() => goToDepth(-1)}
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="m3 10 9-7 9 7v9a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2z" />
        </svg>
      </button>
      {#each folder as segment, i (i)}
        <span class="text-ink-muted" aria-hidden="true">/</span>
        <button
          class="px-1 rounded transition-colors truncate max-w-[12rem] {i === folder.length - 1
            ? 'text-ink font-medium'
            : 'text-ink-muted hover:text-accent'}"
          title={segment}
          onclick={() => goToDepth(i)}
        >
          {segment}
        </button>
      {/each}
    </nav>
  {/if}

  {#if status === "loading"}
    <p class="text-sm text-ink-muted">{t("local.scanning")}</p>
  {:else if status === "error"}
    <p class="text-sm text-error">{errorMessage}</p>
  {:else if !families.length}
    <p class="text-sm text-ink-muted py-8 text-center">{t("local.empty")}</p>
  {:else}
    <!-- Subfolders first, as chips rather than as tiles in the grid: a folder is a place to go, not
         a thing to load into the project, and mixing the two would put them one click apart. Each
         carries what is under it, subfolders included — the number is what tells a shelf worth
         opening from one holding a single stray file. -->
    {#if subfolders.length}
      <div class="flex items-center gap-2 flex-wrap">
        {#each subfolders as [name, count] (name)}
          <AppButton variant="secondary" size="sm" title={name} onclick={() => openFolder(name)} class="max-w-[16rem]">
            <svg viewBox="0 0 24 24" class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M3 7a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.7.9l.8 1.2h7.6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            </svg>
            <span class="truncate">{name}</span>
            <span class="text-xs text-ink-muted tabular-nums shrink-0">{count}</span>
          </AppButton>
        {/each}
      </div>
    {/if}

    <!-- A folder holding only folders is normal and needs no explanation. Nothing at all does: it
         means the category filter emptied this room, and without a word here the grid just vanishes
         and reads as a failure. -->
    {#if !visibleFamilies.length && !subfolders.length}
      <p class="text-sm text-ink-muted py-8 text-center">{t("local.emptyHere")}</p>
    {/if}

    <div class={gridClass}>
      {#each visibleFamilies as f (f.path)}
        <!-- One card, three shapes. List turns it on its side and shrinks the preview to a
             thumbnail beside the name; compact drops the text entirely and leans on the preview,
             with the name kept as the tooltip so a tile is still identifiable. -->
        <button
          class="text-left rounded-surface border border-line hover:border-accent transition-colors overflow-hidden {view ===
          'list'
            ? 'flex items-center gap-3 p-2'
            : 'flex flex-col'}"
          title={view === "compact" ? `${f.name}\n${subtitle(f)}` : f.name}
          onclick={() => (open = f)}
        >
          <!-- Same tile as the cloud card, deliberately: the two grids sit behind one source
               switcher and a different aspect ratio between them reads as the layout shifting when
               you change source. -->
          <div
            class="bg-panel flex items-center justify-center text-ink-muted overflow-hidden shrink-0 {view ===
            'list'
              ? 'w-12 h-12 rounded'
              : 'w-full aspect-[4/3]'}"
            {@attach lazyThumbnail(f)}
          >
            <!-- max-*, not w-full/h-full. The tile is a flex item, so its min-height is auto, and an
                 image asked to fill it resolves its own height against an indefinite parent and
                 falls back to its intrinsic 256px — which then pushes the tile past the aspect ratio
                 it was given. Cards with a preview grew taller than cards without one and the
                 captions across a row stopped lining up. Capping instead of filling also keeps the
                 preview crisp rather than upscaling it, same reasoning as FamilyCard. -->
            {#if thumbByPath[f.path]}
              <img
                src={`data:image/png;base64,${thumbByPath[f.path]}`}
                alt=""
                class="max-w-full max-h-full object-contain"
              />
            {:else}
              <svg
                width={view === "compact" ? 20 : 28}
                height={view === "compact" ? 20 : 28}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                <path d="m3.3 7 8.7 5 8.7-5M12 22V12" />
              </svg>
            {/if}
          </div>

          {#if view !== "compact"}
            <div class="flex flex-col gap-0.5 min-w-0 flex-1 {view === 'list' ? '' : 'p-2.5'}">
              <span class="text-sm font-medium truncate">{f.name}</span>
              <span class="text-xs text-ink-muted tabular-nums truncate">{subtitle(f)}</span>
            </div>
          {/if}
        </button>
      {/each}
    </div>
    {#if truncated}
      <p class="text-xs text-ink-muted text-center">{t("local.truncated")}</p>
    {/if}
  {/if}

  {#if open}
    <LocalFamilyDetail
      path={open.path}
      name={open.name}
      sizeBytes={open.sizeBytes}
      modifiedTicks={open.modifiedTicks}
      onclose={closeDetail}
    />
  {/if}
</div>
